import { Editor } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { NodeViewWrapper } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../../../components/ui/Icon';
import { ChartConfig } from '../Chart';
import { ChartModal } from './ChartModal';
import { ChartPreview } from './ChartPreview';

interface ChartViewProps {
  editor: Editor;
  getPos: () => number;
  node: Node;
  updateAttributes: (attrs: Record<string, any>) => void;
}

const EditButton = ({ onEdit }: { onEdit: () => void }) => {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onEdit();
      }}
      className="flex items-center gap-1 px-2 py-1 text-xs bg-black text-white rounded hover:bg-neutral-800 transition-colors shadow-lg"
    >
      <Icon name="SquarePen" className="w-3 h-3" />
      Editar
    </button>
  );
};

export const ChartView = (props: ChartViewProps) => {
  const { editor, getPos, node, updateAttributes } = props;

  const chartNode = node as Node & {
    attrs: {
      config: ChartConfig;
      imagePath?: string;
    };
  };

  // Se o gráfico não tem imagem salva ainda, abrir direto o modal
  const shouldStartEditing =
    !chartNode.attrs.imagePath && !chartNode.attrs.config?.imagePath;
  const [isModalOpen, setIsModalOpen] = useState(shouldStartEditing);
  const [config, setConfig] = useState(chartNode.attrs.config);
  const [imagePath, setImagePath] = useState(chartNode.attrs.imagePath);

  // Sincronizar estado local com as mudanças no nó
  useEffect(() => {
    setConfig(chartNode.attrs.config);
    setImagePath(chartNode.attrs.imagePath);
  }, [chartNode.attrs.config, chartNode.attrs.imagePath]);

  const onClick = useCallback(() => {
    editor.commands.setNodeSelection(getPos());
  }, [getPos, editor.commands]);

  const onEdit = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Restaurar valores originais ao fechar sem salvar
    setConfig(chartNode.attrs.config);
    setImagePath(chartNode.attrs.imagePath);
  }, [chartNode.attrs.config, chartNode.attrs.imagePath]);

  const onSave = useCallback(
    (newConfig: ChartConfig, newImagePath?: string) => {
      const finalImagePath = newImagePath || chartNode.attrs.imagePath;
      setConfig(newConfig);
      setImagePath(finalImagePath);
      updateAttributes({
        config: newConfig,
        imagePath: finalImagePath,
      });
      setIsModalOpen(false);
    },
    [updateAttributes, chartNode.attrs.imagePath]
  );

  // Se temos uma imagem salva, mostrar a imagem
  const currentImagePath =
    imagePath || chartNode.attrs.imagePath || config.imagePath;

  return (
    <>
      <NodeViewWrapper>
        {currentImagePath && currentImagePath.trim() ? (
          // Se temos uma imagem salva, mostrar a imagem
          <div
            className="chart-container rounded-lg bg-white cursor-pointer hover:shadow-sm transition-shadow relative group"
            onClick={onClick}
          >
            <img
              src={currentImagePath}
              alt={config.title || 'Gráfico'}
              className="h-auto mx-auto block rounded-lg"
            />

            {/* Botão Editar - aparece no hover */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <EditButton onEdit={onEdit} />
            </div>
          </div>
        ) : (
          // Se não temos imagem, mostrar preview com botão de editar
          <div
            className="chart-preview border border-gray-300 border-2 border-dashed rounded-lg p-4 bg-white cursor-pointer hover:border-gray-400"
            onClick={onClick}
          >
            <div className="flex justify-end items-center mb-4">
              <EditButton onEdit={onEdit} />
            </div>

            <ChartPreview config={config} />
          </div>
        )}
      </NodeViewWrapper>

      {/* Modal de edição */}
      <ChartModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        config={config}
        onSave={onSave}
      />
    </>
  );
};

export default ChartView;
